<script setup lang="ts">
import device from '@@/bits/device'
import { defineAsyncComponent } from '@@/bits/vue'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceOnboardingDemoPadletPanelStore } from '@@/pinia/surface_onboarding_demo_padlet_panel_store'
import { useSurfacePermissionsStore } from '@@/pinia/surface_permissions'
import { useSurfacePostsDragAndDropStore } from '@@/pinia/surface_posts_drag_and_drop'
import { useSurfaceTranslationsStore } from '@@/pinia/surface_translations_store'
import { useSurfaceWishArrangementStore } from '@@/pinia/surface_wish_arrangement'
import type { Id } from '@@/types'
import SurfaceHeader from '@@/vuecomponents/SurfaceHeader.vue'
import { useDoubleClickToPost } from '@@/vuecomposables/surface_double_click_to_post'
import { GroupByTypes } from '@padlet/arvo'
import { storeToRefs } from 'pinia'

const SurfaceAddSectionButton = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceAddSectionButton.vue'))
const SurfacePostsStream = defineAsyncComponent(() => import('@@/vuecomponents/SurfacePostsStream.vue'))

withDefaults(
  defineProps<{
    reactionIdsByPostId: Record<Id, Id[]>
    commentIdsByPostId: Record<Id, Id[]>
  }>(),
  {
    reactionIdsByPostId: () => ({}),
    commentIdsByPostId: () => ({}),
  },
)

defineEmits(['scroll'])

const { xHeader, backgroundLuminance, isReadOnly, isPostSizeStandard, isPostSizeWide, isFrozen } = storeToRefs(
  useSurfaceStore(),
)
const { canIModerate } = storeToRefs(useSurfacePermissionsStore())
const { wishGroupBy } = storeToRefs(useSurfaceWishArrangementStore())
const { isDemoPadletPanelDesktop } = storeToRefs(useSurfaceOnboardingDemoPadletPanelStore())
const { isDraggingPostInNonManuallySortedWall } = storeToRefs(useSurfacePostsDragAndDropStore())
const { isViewingTranslatedPadlet } = storeToRefs(useSurfaceTranslationsStore())
if (!device.mobile) useDoubleClickToPost()
</script>

<template>
  <div
    id="wall-container"
    :class="{
      'scrollbar-photo': backgroundLuminance === 'light',
      'scrollbar-photo-dark': backgroundLuminance === 'dark',
      'overflow-x-hidden': !isReadOnly,
    }"
    @scroll="$emit('scroll', $event)"
  >
    <SurfaceHeader v-if="xHeader" />

    <SurfacePostsStream
      class="mt-3 tablet-portrait:mt-4 pb-8"
      :reaction-ids-by-post-id="reactionIdsByPostId"
      :comment-ids-by-post-id="commentIdsByPostId"
    />
    <div
      v-if="
        wishGroupBy === GroupByTypes.Section &&
        canIModerate &&
        !isReadOnly &&
        !isDraggingPostInNonManuallySortedWall &&
        !isFrozen &&
        !isViewingTranslatedPadlet
      "
      class="flex flex-col items-center pb-16"
    >
      <div
        v-if="!isDemoPadletPanelDesktop"
        :class="[
          'max-w-full',
          isPostSizeStandard && 'tablet-portrait:w-135',
          isPostSizeWide && 'tablet-portrait:w-187',
        ]"
      >
        <SurfaceAddSectionButton class="ms-4 tablet-portrait:ms-0" />
      </div>
    </div>
  </div>
</template>
